import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

import type { FundManager } from '@/integrations/supabase/metadata.models';

export type { FundManager };

/**
 * Hook to fetch all fund managers ordered alphabetically by name.
 */
export function useFundManagers() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['fund-managers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fund_managers')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      return data as FundManager[];
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to create a new fund manager by name.
 */
export function useCreateFundManager() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const { data, error } = await supabase
        .from('fund_managers')
        .insert({ name })
        .select()
        .single();

      if (error) throw error;
      return data as FundManager;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fund-managers'] });
    },
  });
}
